import { IDrawerStyles, useDrawerStyles } from "..";
import { DrawerStyled } from "./drawerStyled";
import Form, { FormProps as vfxFormProps } from "../../form/form";
import { DrawerProps } from "../drawer";
import { DrawerActionButtonProps } from "./template";
import { GuidHelper } from "../../../utils/helpers";
import * as yup from "yup";
import { VfxLib } from "../..";
import { useRef } from "react";
import { FormikProps, FormikTouched, FormikValues } from "formik";
import { DrawerFormSaveResult } from "../../../utils/lib";

type TouchedTree = boolean | TouchedTree[] | { [key: string]: TouchedTree };

const getTouchedFromErrors = (value: unknown): TouchedTree => {
    if (Array.isArray(value)) {
        return value.map((item) => getTouchedFromErrors(item));
    }

    if (value && typeof value === "object") {
        return Object.keys(value as Record<string, unknown>).reduce<
            Record<string, TouchedTree>
        >((touched, key) => ({
            ...touched,
            [key]: getTouchedFromErrors((value as Record<string, unknown>)[key]),
        }), {});
    }

    return true;
};

export interface FormDrawerProps extends DrawerProps {
    title?: string;
    initialValues?: any | undefined;
    validationSchema?: yup.Schema | undefined;
    controlStyles?: IDrawerStyles | undefined;
    enableReinitialize?: boolean;
    onCopy?: (values: any) => void | Promise<void> | undefined;
    onSave?: (values: any) => DrawerFormSaveResult | undefined;
    on_Submit?: (values: any) => DrawerFormSaveResult | undefined;
    onDelete?: React.MouseEventHandler<HTMLButtonElement> | undefined;
    onDeactivate?: React.MouseEventHandler<HTMLButtonElement> | undefined;
}

const ComponentProps = (props: FormDrawerProps): DrawerProps => {
    const componentProps = Object.assign({}, props);
    delete componentProps.onCopy;
    delete componentProps.onSave;
    delete componentProps.on_Submit;
    delete componentProps.onDelete;
    delete componentProps.onDeactivate;

    delete componentProps.initialValues;
    delete componentProps.validationSchema;
    delete componentProps.controlStyles;
    delete componentProps.enableReinitialize;

    return componentProps;
};

const FormProps = (props: FormDrawerProps): vfxFormProps => {
    const formProps: vfxFormProps = {
        children: props.children,
        disabled: props.disabled,
        initialValues: props.initialValues,
        validationSchema: props.validationSchema,
        enableReinitialize: props.enableReinitialize,
    };

    return formProps;
};

export const DrawerForm: React.FC<FormDrawerProps> = (props) => {
    const styles: IDrawerStyles = props.controlStyles ?? useDrawerStyles();
    const formId: string = "form_" + GuidHelper.generateGuid();
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const headerButtons: DrawerActionButtonProps[] = [
        VfxLib.drawerActionButtons({
            drawerStyles: styles,
            onClick: props.onHide,
            formId: formId,
        }).close,
    ];

    const footerButtons: DrawerActionButtonProps[] = [
        VfxLib.drawerActionButtons({
            drawerStyles: styles,
            onClick: props.onDelete,
            formId: formId,
        }).delete,
        VfxLib.drawerActionButtons({
            drawerStyles: styles,
            onClick: props.onDeactivate,
            formId: formId,
        }).deactivate,
    ];

    if (props.onSave !== undefined) {
        footerButtons.push(
            VfxLib.drawerActionButtons({
                drawerStyles: styles,
                onClick: () => handleSaveSubmit("save", props),
                formId: formId,
            }).saveButton
        );
    }

    if (props.on_Submit !== undefined) {
        footerButtons.push(
            VfxLib.drawerActionButtons({
                drawerStyles: styles,
                onClick: () => handleSaveSubmit("submit", props),
                formId: formId,
            }).submit
        );
    }

    const handleSaveSubmit = async (
        action: "save" | "submit",
        props: FormDrawerProps
    ) => {
        const formik = formikRef.current;
        if (!formik) return;

        // Validate form
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            formik.setTouched(
                getTouchedFromErrors(errors) as FormikTouched<FormikValues>,
                true
            );
            return;
        }

        const { values } = formik;

        if (action === "save" && props.onSave) {
            await props.onSave(values);
        } else if (action === "submit" && props.on_Submit) {
            await props.on_Submit(values);
        }
    };

    return (
        <DrawerStyled
            {...ComponentProps(props)}
            headerbuttons={headerButtons}
            footerbuttons={footerButtons}
        >
            <Form id={formId} {...FormProps(props)} formikRef={formikRef} />
        </DrawerStyled>
    );
};